<template>
  <div
    class="sv-appt"
    :class="[`sv-appt--${tier}`, `sv-appt--${bucket}`, { 'sv-appt--broken': broken }]"
    :style="blockStyle"
    :data-test="`sv-appt-${appt.AptNum}`"
    :aria-label="hoverText"
    role="button"
    tabindex="0"
    @click="$emit('select', appt)"
    @keydown.enter="$emit('select', appt)"
    @keydown.space.prevent="$emit('select', appt)"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focus="onFocus"
    @blur="onLeave"
  >
    <div class="sv-appt__row1" :style="row1Style">
      <span class="sv-appt__dot" :class="`sv-appt__dot--${bucket}`" aria-hidden="true"></span>
      <span class="sv-appt__name">{{ patientName }}</span>
      <span v-if="showStatus" class="sv-appt__status" :class="`sv-appt__status--${bucket}`">
        {{ statusText }}
      </span>
    </div>

    <!--
      Alert icons sit on their own row so they never compete with the patient's name for space.
      They survive every density tier, INCLUDING the smallest: a 22px block still has room for a
      row of 15px icons, and "this patient needs premedication" is worth more than the procedure
      description that the same block already dropped.

      On the TINY tier they are absolutely positioned into the top-right corner instead, sharing
      the name's line — so `row1Style` reserves exactly their width. Without that the name runs
      underneath them and its last characters are unreadable, which is the same class of bug as a
      long status squeezing the name out.

      The checklist chip (touch only) ends this row, never the name's row, for the same reason; the
      TINY tier has no room for it at all, so it is not drawn there (the block itself still opens
      the Patient Panel).
    -->
    <div v-if="alertKeys.length" class="sv-appt__alerts" data-test="sv-appt-alerts">
      <AlertBadge
        v-for="key in alertKeys"
        :key="key"
        :alert-key="key"
        :detail="alertDetails[key]"
        :hover-mode="hoverMode"
        @hover-alert="onHoverAlert"
      />
      <ChecklistChip
        v-if="tier !== 'tiny'"
        class="sv-appt__chip"
        :totals="checklistTotals"
        :category-count="categoryCount"
        :mode="checklistMode"
        @toggle="onChipToggle"
      />
    </div>

    <!-- The procedure CODES a weight heavier than their words (layout.js procedureParts — lossless). -->
    <p v-if="showProcedures" class="sv-appt__procs"><template v-for="(part, i) in procParts"><span v-if="part.code" :key="i" class="sv-appt__code">{{ part.text }}</span><template v-else>{{ part.text }}</template></template></p>

    <!--
      The appointment note (Open Dental's "Appointment Note"), as many WHOLE lines as the block has
      room for and never a half-clipped one (a clipped line reads as a rendering bug — see the
      procedures' note in the stylesheet). The box takes ALL the room left under the name, the icons
      and the procedures (flex: 1 1 0), so its height never depends on the note; measureNote() reads
      that height and floors it to lines (apptNote.js noteLinesThatFit) for the clamp. No formula
      could know the room in advance: it depends on how the procedures and the icons wrap.
      Normal tier only. NOT gated by the gear's "show procedures" — that switch is about procedures.
      The box is drawn with no room too, so a resize can bring the note back.
    -->
    <div v-if="showNote" ref="noteBox" class="sv-appt__note-box" data-test="sv-appt-note-box">
      <p
        v-if="noteLines > 0"
        class="sv-appt__note"
        data-test="sv-appt-note"
        :data-lines="noteLines"
        :style="noteStyle"
      >{{ note }}</p>
    </div>

    <!--
      Broken appointments are drawn with a big X at partial opacity rather than hidden or greyed
      out: the office still needs to read who it was and what it was for (that is the whole point
      of seeing a broken slot), so the block content stays legible underneath.
    -->
    <template v-if="broken">
      <svg class="sv-appt__x" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <line x1="4" y1="4" x2="96" y2="96" />
        <line x1="96" y1="4" x2="4" y2="96" />
      </svg>
      <span v-if="tier !== 'tiny'" class="sv-appt__broken-tag">BROKEN</span>
    </template>

    <!--
      The hover card that used to live here is gone: it was `pointer-events: none` and could be read
      and never touched. The visit checklist (ChecklistLayer.vue, ONE per page) replaced it, so this
      block only reports the pointer — `card-enter` / `card-leave` / `alert-focus` / `open-checklist`
      — and the layer decides what to show and where.
    -->
  </div>
</template>

<script>
/**
 * One appointment block.
 *
 * ⛔ No prop may be named `patNum`, `siteID` or `loading`: mixin.js defines computeds with those
 * names and a computed SILENTLY REPLACES a same-named prop, so the value would vanish with no
 * error. Guarded by cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import { resolveBucket, statusLabel, isBroken } from '@/utils/scheduleView/statusBuckets';
import {
  formatMinutes, minutesFromMidnight, appointmentLength, patientDisplayName, procedureParts,
} from '@/utils/scheduleView/layout';
import { alertsForPatient, blockAlertKeys } from '@/utils/scheduleView/alerts';
import { appointmentNote, noteOneLine, noteLinesThatFit } from '@/utils/scheduleView/apptNote';
import { ALERT_KEYS } from '@/utils/scheduleView/settingsForm';
import AlertBadge from './AlertBadge.vue';
import ChecklistChip from './ChecklistChip.vue';

export default {
  name: 'AppointmentBlock',

  components: { AlertBadge, ChecklistChip },

  props: {
    /** One appointment from /schedule-view/day. */
    appt: { type: Object, required: true },
    /** 'normal' | 'compact' | 'tiny' — how much the block is tall enough to say. */
    tier: { type: String, default: 'normal' },
    /** Resolved settings from the same payload. Never a local default — see the store. */
    viewSettings: { type: Object, required: true },
    /** DefNum -> bucket, already merged with the office's overrides. */
    bucketMap: { type: Object, default: () => ({}) },
    /**
     * Hex colour of the left stripe: the provider's Open Dental "Appointment Color" (the hygienist's
     * on a hygiene visit), or the neutral grey when there is none. Chosen by the grid —
     * provColor.js providerStripeColor — so the block stays a presentational component.
     */
    accent: { type: String, default: '#9ca4a2' },
    /** Absolute geometry within the column, in px. */
    top: { type: Number, required: true },
    height: { type: Number, required: true },
    /** Side-by-side lane when a chair is double-booked. */
    lane: { type: Number, default: 0 },
    laneCount: { type: Number, default: 1 },
    /**
     * The whole /schedule-view/alerts payload, or null before it arrives. Passed whole rather than
     * pre-sliced so the block can consult `ran` — an alert that FAILED must be absent everywhere,
     * not absent on some blocks where it would read as "this patient is fine".
     */
    alertPayload: { type: Object, default: null },
    /**
     * This appointment's visit checklist from the page ({ categories, totals }), or null. The page
     * builds every checklist ONCE per render from the same alert keys this block draws.
     */
    checklist: { type: Object, default: null },
    /** 'hover' | 'pop' | 'sheet', from the layer. Hover draws no chip and drops the badges' title. */
    checklistMode: { type: String, default: 'hover' },
  },

  data() {
    // How many whole lines of note fit right now. 0 until measured (and wherever nothing can be
    // measured), which draws no note rather than a guess.
    return { noteLines: 0 };
  },

  computed: {
    bucket() {
      return resolveBucket(this.appt, this.bucketMap);
    },

    /**
     * Only alerts the office has switched ON, and only ones the server actually computed — the ONE
     * rule (alerts.js blockAlertKeys), shared with the phone card, the checklist and the table.
     * ⛔ Do not filter visibleAlertKeys here again: a second copy is how an icon and a checklist
     * line come to disagree.
     */
    alertKeys() {
      return blockAlertKeys(this.alertPayload, this.appt.PatNum, this.viewSettings, ALERT_KEYS);
    },

    hoverMode() {
      return this.checklistMode === 'hover';
    },

    checklistTotals() {
      return (this.checklist && this.checklist.totals) || { todo: 0, done: 0, fyi: 0 };
    },

    categoryCount() {
      return this.checklist && this.checklist.categories ? this.checklist.categories.length : 0;
    },

    alertDetails() {
      return alertsForPatient(this.alertPayload, this.appt.PatNum);
    },

    broken() {
      return isBroken(this.appt);
    },

    statusText() {
      return statusLabel(this.appt, this.bucket);
    },

    /** What the office calls the patient — one definition, shared with the phone card. */
    patientName() {
      return patientDisplayName(this.appt);
    },

    /**
     * Not on a broken appointment: the BROKEN tag sits in the same top-right corner and the two
     * printed over each other (found in the Phase 6 audit, 2026-09-25). "Broken" is the status
     * that matters; the confirmation it had before is still in the checklist card.
     */
    showStatus() {
      return this.viewSettings.showConfirmation && this.tier !== 'tiny' && !this.broken;
    },

    showProcedures() {
      return this.viewSettings.showProcedures
        && this.tier === 'normal'
        && !!this.appt.ProcDescript;
    },

    procParts() {
      return procedureParts(this.appt.ProcDescript);
    },

    /** The visit's note, or '' — the ONE definition (apptNote.js), shared by every surface. */
    note() {
      return appointmentNote(this.appt);
    },

    /** Only the normal tier has the room, and only a visit that HAS a note gets a box. */
    showNote() {
      return this.tier === 'normal' && !!this.note;
    },

    /** The clamp, as a custom property the stylesheet reads (`-webkit-line-clamp: var(--sv-note-lines)`). */
    noteStyle() {
      return { '--sv-note-lines': String(this.noteLines) };
    },

    /** Screen-reader label — what the checklist card shows in full, in one line. */
    hoverText() {
      const start = minutesFromMidnight(this.appt.AptDateTime);
      const parts = [this.patientName];
      if (start !== null) {
        parts.push(`${formatMinutes(start)} – ${formatMinutes(start + appointmentLength(this.appt))}`);
      }
      if (this.appt.ProcDescript) parts.push(this.appt.ProcDescript);
      if (this.note) parts.push(`Note: ${noteOneLine(this.note)}`);
      parts.push(this.statusText);
      if (this.broken) parts.push('Broken');
      return parts.join(' · ');
    },

    /**
     * On the shortest blocks the alert icons are absolutely positioned in the top-right corner, on
     * the same line as the name — so without this the name runs UNDERNEATH them and the last few
     * characters are unreadable. Reserving exactly the icons' width keeps the name legible and
     * keeps the icons where a scanning eye expects them.
     */
    row1Style() {
      if (this.tier !== 'tiny' || !this.alertKeys.length) return null;
      const ICON_WIDTH = 15;
      const GAP = 2;
      const EDGE = 3;
      const reserved = this.alertKeys.length * (ICON_WIDTH + GAP) + EDGE;
      return { paddingRight: `${reserved}px` };
    },

    blockStyle() {
      // Lanes split the column's width when a chair is double-booked; with one lane this is the
      // whole column, so the common case is unaffected.
      const count = Math.max(1, this.laneCount);
      const widthPct = 100 / count;
      return {
        top: `${this.top}px`,
        height: `${this.height}px`,
        left: `calc(${this.lane * widthPct}% + 3px)`,
        width: `calc(${widthPct}% - 6px)`,
        borderLeftColor: this.accent,
      };
    },
  },

  mounted() {
    this.syncNote();
  },

  // The room changes with the props (a live refresh gives the visit another procedure, the note
  // appears, the lane count changes the width the procedures wrap in) — measure again.
  updated() {
    this.syncNote();
  },

  beforeDestroy() {
    this.stopNoteWatch();
    // A block can be removed by a live refresh while the pointer is still over it, and no
    // mouseleave ever fires for an element that stopped existing. Telling the layer starts its
    // grace timer, so the card is not left floating over the grid with nothing under it.
    this.$emit('card-leave', { appt: this.appt });
  },

  methods: {
    /**
     * Keep the measurement alive for as long as there is a note box: watch the box itself (its
     * height is exactly "the room left", so it changes when the column narrows and the procedures
     * re-wrap, when a web font arrives, or when the window resizes), then measure now.
     */
    syncNote() {
      const box = this.$refs.noteBox || null;
      if (box !== this.noteWatched) {
        this.stopNoteWatch();
        if (box) this.startNoteWatch(box);
      }
      this.measureNote();
    },

    startNoteWatch(box) {
      this.noteWatched = box;
      if (typeof window.ResizeObserver === 'function') {
        this.noteObserver = new window.ResizeObserver(() => this.measureNote());
        this.noteObserver.observe(box);
      } else {
        // An older iPad's Safari has no ResizeObserver: the window resizing is the next best signal.
        window.addEventListener('resize', this.measureNote);
      }
    },

    stopNoteWatch() {
      if (this.noteObserver) {
        this.noteObserver.disconnect();
        this.noteObserver = null;
      }
      window.removeEventListener('resize', this.measureNote);
      this.noteWatched = null;
    },

    /**
     * The note box's USED height, floored to whole lines. getComputedStyle, not
     * getBoundingClientRect: the rect is scaled by any CSS transform above the page (Cypress shrinks
     * the app's iframe), the computed height is the layout's own px. Only writes on a change, so the
     * `updated` hook that calls this cannot loop.
     */
    measureNote() {
      const box = this.$refs.noteBox;
      const lines = box ? noteLinesThatFit(parseFloat(window.getComputedStyle(box).height)) : 0;
      if (lines !== this.noteLines) this.noteLines = lines;
    },

    /** The pointer reached the block. The layer waits for it to REST before opening anything. */
    onEnter() {
      this.$emit('card-enter', { appt: this.appt, el: this.$el, via: 'hover' });
    },

    /**
     * Keyboard focus reached the block: opens at once (a tabbing user is not "passing by").
     *
     * ⛔ KEYBOARD focus only. A mouse press focuses the block too (it has tabindex 0), and opening
     * the card at that instant would flash it for the length of the click, just before the click
     * opens the Patient Panel over it. `:focus-visible` is the browser's own "this came from the
     * keyboard"; a browser that cannot parse it (an old iPad) gets the card on any focus, which is
     * how this behaved before.
     */
    onFocus() {
      let keyboard = true;
      try {
        keyboard = !!(this.$el && this.$el.matches(':focus-visible'));
      } catch (e) {
        keyboard = true;
      }
      if (!keyboard) return;
      this.$emit('card-enter', { appt: this.appt, el: this.$el, via: 'focus' });
    },

    /** The pointer left, or focus did. */
    onLeave() {
      this.$emit('card-leave', { appt: this.appt });
    },

    /** An alert icon was hovered: the card highlights and scrolls to that icon's category. */
    onHoverAlert(key) {
      this.$emit('alert-focus', { appt: this.appt, key });
    },

    /** The touch chip: open (or close) this block's checklist. It never also selects the block. */
    onChipToggle() {
      this.$emit('open-checklist', { appt: this.appt, el: this.$el });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

// White, a 1px line, 10px corners, the soft shadow (a raised one on hover) and a 5px stripe in the
// provider's Open Dental colour — set inline by `blockStyle`; this grey is only the first paint.
.sv-appt {
  position: absolute;
  box-sizing: border-box;
  overflow: hidden;
  background: $sv-surface;
  border: 1px solid $sv-line;
  border-left: 5px solid $sv-ink-4;
  border-radius: $sv-radius;
  padding: 6px 7px;
  cursor: pointer;
  text-align: left;
  box-shadow: $sv-shadow-1;
  transition: box-shadow 0.12s ease;

  &:hover,
  &:focus {
    box-shadow: $sv-shadow-2;
    outline: none;
    z-index: 3;
  }

  &:focus-visible {
    outline: 2px solid $sv-brand;
    outline-offset: 1px;
  }
}

.sv-appt--compact {
  padding: 4px 6px;
}

.sv-appt--tiny {
  padding: 2px 6px;
}

// The NORMAL tier is a column so the note box can take whatever room is left under the name, the
// icons and the procedures. The three rows above it keep their natural height (`flex: 0 0 auto`):
// a column flex container would otherwise SHRINK them — the procedures (overflow: hidden) down to
// nothing — on a block that is already full.
.sv-appt--normal {
  display: flex;
  flex-direction: column;
}

.sv-appt--normal > .sv-appt__row1,
.sv-appt--normal > .sv-appt__alerts,
.sv-appt--normal > .sv-appt__procs {
  flex: 0 0 auto;
}

.sv-appt__row1 {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.sv-appt__dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $sv-status-unconfirmed;

  &--confirmed { background: $sv-status-confirmed; }
  &--arrived { background: $sv-status-arrived; }
  &--completed { background: $sv-status-completed; }
  &--unconfirmed { background: $sv-status-unconfirmed; }
}

/**
 * ⛔ THE NAME ALWAYS WINS. Do not give `__status` a rigid `flex: 0 0 auto` again.
 *
 * It used to be exactly that way round — the name shrank and the status did not — and on a real
 * office's schedule that was a bug, not a cosmetic nit. Open Dental status names are typed by the
 * practice and are often long and shouty ("APPOINTMENT CONFIRMED"). A rigid status took the whole
 * row and ellipsised the patient away, so staff looking at the 10am chair could not tell WHO was
 * in it. The status is a detail the coloured dot already carries; the name is the entire reason
 * to look at the block. Found on dev 2026-09-20.
 */
.sv-appt__name {
  flex: 1 1 auto;
  // Roughly "Lastname, F" at 12.5px. Below this a name stops being an identification, and the
  // status has to give up its space instead.
  min-width: 68px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  color: $sv-ink;
}

.sv-appt__status {
  // Shrinkable, and never more than a third of the row. A clipped status still reads as a status
  // because the dot beside it carries the colour; a clipped NAME reads as the wrong patient. The
  // checklist card carries the office's full wording either way.
  flex: 0 1 auto;
  min-width: 0;
  max-width: 33%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  // The confirmation wording is a PILL (the design's `.conf`): the bucket's tint behind its shade.
  padding: 1px 6px;
  border-radius: $sv-radius-pill;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

@each $key, $c in $sv-pill {
  .sv-appt__status--#{$key} {
    color: map-get($c, shade);
    background: map-get($c, tint);
  }
}

.sv-appt__alerts {
  display: flex;
  gap: 2px;
  margin-top: 2px;
  // Wrapping rather than scrolling or clipping: a patient with six alerts on a narrow column
  // shows all six on two rows. The block is already sized from the appointment's length, so the
  // overflow is hidden by the block — an icon that is half-cut still reads as "there is more".
  flex-wrap: wrap;
  // The row sits ABOVE the broken-appointment X so the icons stay readable through the overlay.
  position: relative;
  z-index: 2;
}

.sv-appt--tiny .sv-appt__alerts {
  // On the shortest blocks the icons share the name's line instead of taking a row of their own.
  position: absolute;
  top: 2px;
  right: 3px;
  margin-top: 0;
  flex-wrap: nowrap;
}

.sv-appt__procs {
  margin: 2px 0 0;
  font-size: 11.5px;
  line-height: 1.25;
  color: $sv-ink-3;
  // Two lines, then ellipsis — a clipped third line reads as a rendering bug.
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// ⛔ The appointment note: WHOLE lines only. The box is the room left in the block (flex: 1 1 0 —
// its height never depends on the note, so measuring it cannot feed back), clipped; the note inside
// is clamped to `floor(room / line)` lines (measureNote → --sv-note-lines). Both numbers are exact
// only because a line is a WHOLE number of px tall: `line-height` here is NOTE_LINE_PX in
// utils/scheduleView/apptNote.js (a node-test holds the two together). Italic, the same muted ink as
// the procedures. `pre-line` keeps the office's own line breaks; `overflow-wrap: anywhere` stops a
// long unbroken word widening the block.
.sv-appt__note-box {
  flex: 1 1 0;
  min-height: 0;
  margin-top: 3px;
  overflow: hidden;
}

.sv-appt__note {
  margin: 0;
  font-size: 11.5px;
  font-style: italic;
  line-height: 14px;
  color: $sv-ink-3;
  white-space: pre-line;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: var(--sv-note-lines);
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// The code ("D2950") in the sans face at 600 — NOT mono — and a shade darker than its words.
.sv-appt__code {
  font-weight: 600;
  color: $sv-ink-2;
}

.sv-appt__x {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.55;

  line {
    stroke: $sv-broken;
    stroke-width: 3;
    vector-effect: non-scaling-stroke;
  }
}

.sv-appt__broken-tag {
  position: absolute;
  top: 2px;
  right: 4px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: $sv-broken;
  pointer-events: none;
}

.sv-appt--broken {
  background: rgba(255, 255, 255, 0.78);
}

// The touch chip sits on the icons' row. It is 20px tall on a 15px row, so it is pulled up and down
// by the difference — otherwise a COMPACT block (about 45px) would grow its row and clip the bottom.
.sv-appt__chip {
  margin-top: -3px;
  margin-bottom: -2px;
}
</style>
